/** @jsxImportSource @emotion/react */
import "./App.css";
import BeaconList from "../beacon/BeaconList";
import OrganizationSettingsUpsert from "../organization/OrganizationSettingsUpsert";
import PromotionCodeList from "../promotion-code/PromotionCodeList";
import { UserMonitorEventList } from "../user-monitor-event/UserMonitorEventList";
import * as React from "react";
import { useSelector } from "react-redux";
import { routePatterns } from "../../appRoutingHelpers";
import { UserProfile } from "../user-profile/UserProfile";
import * as authSelectors from "../auth/authSelectors";
import { Login } from "../auth/Login";
import UserList from "../user/UserList";
import { VenueList } from "../venue/VenueList";
import VenueUpsert from "../venue/VenueUpsert";
import { OrganizationMonitorEventList } from "../user-monitor-event/OrganizationMonitorEventList";
import EmailTemplateList from "../email-template/EmailTemplateList";
import { EmailTemplateUpsert } from "../email-template/EmailTemplateUpsert";
import AdminUserList from "../admin-user/AdminUserList";
import { AdminUserUpsert } from "../admin-user/AdminUserUpsert";
import MemberCardTypeList from "../member-card-type/MemberCardTypeList";
import MemberCardTypeUpsert from "../member-card-type/MemberCardTypeUpsert";
import PartnershipList from "../partnership/PartnershipList";
import { PartnershipUpsert } from "../partnership/PartnershipUpsert";
import UserTermList from "../user-term/UserTermList";
import UserTermUpsert from "../user-term/UserTermUpsert";
import UserTermVersionList from "../user-term/UserTermVersionList";
import { UserTermVersionUpsert } from "../user-term/UserTermVersionUpsert";
import { ShopList } from "../shop/ShopList";
import ShopUpsert from "../shop/ShopUpsert";
import { ShopSettings } from "../shop/ShopSettings";
import ProductUpsert from "../products/ProductUpsert";
import ProductCategoryList from "../product-categories/ProductCategoryList";
import ProductCategoryUpsert from "../product-categories/ProductCategoryUpsert";
import { ProductAdditionsList } from "../shop/ProductAdditionsList";
import ProductAdditionsUpsert from "../shop/ProductAdditionsUpsert";
import OrderList from "../shop/orders/OrderList";
import OrderShow from "../shop/orders/OrderShow";
import VoucherTypeList from "../voucher-type/VoucherTypeList";
import VoucherTypeUpsert from "../voucher-type/VoucherTypeUpsert";
import OrganizationsList from "../organization/OrganizationsList";
import OrganizationUpsert from "../organization/OrganizationUpsert";
import PunchCardsList from "../shop/punch-cards/PunchCardsList";
import { PunchCardUpsert } from "../shop/punch-cards/PunchCardUpsert";
import { Dashboard } from "../dashboard/Dashboard";
import { DeliveryRulesList } from "../delivery-rules/DeliveryRulesList";
import { ChallengesList } from "../challenges/challenge-list/ChallengesList";
import { ChallengesUpsert } from "../challenges/challenge-upsert/ChallengesUpsert";
import { AppHome } from "../shop/app-home/AppHome";
import { AppHomepageUpsert } from "../shop/app-home/app-homepage/AppHomepageUpsert";
import { AppNewsUpsert } from "../shop/news/NewsUpsert";
import { CtasUpsert } from "../shop/app-home/ctas/CtasUpsert";
import { NewsList } from "../shop/news/NewsList";
import { MenuList } from "../menus/MenuList";
import { MenuEdit } from "../menus/MenuUpsert";
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import PromotionCodeUpsert from "../promotion-code/PromotionCodeUpsert";
import { BeaconView } from "../beacon/BeaconView";
import { AppShell } from "./AppShell";
import { AppProtectedRoutes } from "./AppProtectedRoutes";
import { AppUnprotectedRoutes } from "./AppUnprotectedRoutes";
import { SelectOrganizationList } from "../auth/SelectOrganizationList";
import { AppLayout } from "./AppLayout";
import { NotFound } from "./NotFound";
import { ProductsList } from "../products/ProductsList";
import { IntegrationsList } from "../integrations/IntegrationsList";
import { MenuUpsertLayout } from "../menus/MenuUpsertLayout";
import { MenuRecommendedProductsUpsert } from "../menus/MenuRecommendedProductsUpsert";
import { ApiKeysList } from "../api-keys/ApiKeysList";
import { Reports } from "../reports/Reports";
import { KitchenCategoriesList } from "../kitchen-categories/KitchenCategoriesList";
import { PackingItemsList } from "../packing-items/PackingItemsList";
import { ReportDetails } from "../reports/ReportDetails";
import { KioskConfigurationsList } from "../kiosk-configurations/KioskConfigurationsList";
import { MenuMenuAvailabilityUpsert } from "../menus/MenuMenuAvailabilityUpsert";
import { PaymentProviderConfigurations } from "../payment-provider-configurations/PaymentProviderConfigurations";
import { ProductTagsList } from "../product-tags/ProductTagsList";
import { DeliveryProviderConfigurations } from "../delivery-provider-configurations/DeliveryProviderConfigurations";
import { AutoCampaignsLayout } from "../auto-campaigns/AutoCampaignsLayout";
import { CampaignsPage } from "../auto-campaigns/campaigns/CampaignsPage";
import { EditCampaignPage } from "../auto-campaigns/campaigns/edit";
import { NewCampaignPage } from "../auto-campaigns/campaigns/new";
import { ProductGroupsPage } from "../auto-campaigns/product-groups";
import { EditProductGroupsPage } from "../auto-campaigns/product-groups/edit";
import { NewProductGroupsPage } from "../auto-campaigns/product-groups/new";
import { MenuCampaigns } from "../menus/MenuCampaigns";
import { AddMenuCampaigns } from "../menus/AddMenuCampaigns";
import { NewAdyenAccountHolderPage } from "../adyen-account-holders/new";
import { EditAdyenAccountHolderPage } from "../adyen-account-holders/adyen-account-holder/edit";
import { AdyenAccountHoldersPage } from "../adyen-account-holders/AdyenAccountHoldersPage";
import { AdyenAccountHolderPage } from "../adyen-account-holders/adyen-account-holder";
import { AccountHolderCapabilities } from "../adyen-account-holders/adyen-account-holder/capabilities/AccountHolderCapabilities";
import { EditBusinessLinePage } from "../adyen-account-holders/adyen-account-holder/edit-business-line";
import { NewBusinessLinePage } from "../adyen-account-holders/adyen-account-holder/new-business-line";
import { EditStorePage } from "../adyen-account-holders/adyen-account-holder/edit-store";
import { NewStorePage } from "../adyen-account-holders/adyen-account-holder/new-store";
import { NewBalanceAccountPage } from "../adyen-account-holders/adyen-account-holder/new-balance-account";
import { InspectBalanceAccountPage } from "../adyen-account-holders/adyen-account-holder/inspect-balance-account";
import { InspectSplitConfiguration } from "../adyen-account-holders/adyen-account-holder/inspect-split-configuration";
import { AddNewShopPaymentProvider } from "../shop/AddNewShopPaymentProvider";
import { UpdateShopPaymentProvider } from "../shop/UpdateShopPaymentProvider";

interface Props {}

export const App: React.FC<Props> = () => {
  const hasAccessToBeacons = useSelector(authSelectors.hasAccessToBeacons);
  const hasAccessToPromoCodes = useSelector(
    authSelectors.hasAccessToPromoCodes
  );
  const hasAccessToPartnerships = useSelector(
    authSelectors.hasAccessToPartnerships
  );
  const hasAccessToOrgUISettings = useSelector(
    authSelectors.hasAccessToUiSettings
  );
  const hasAccessToUsers = useSelector(authSelectors.hasAccessToUsers);
  const hasAccessToVenues = useSelector(authSelectors.hasAccessToVenues);
  const hasAccessToEmailTemplates = useSelector(
    authSelectors.hasAccessToEmailTemplates
  );
  const hasAccessToAdmins = useSelector(authSelectors.hasAccessToAdmins);
  const hasAccessToMemberCardTypes = useSelector(
    authSelectors.hasAccessToMemberCardTypes
  );
  const hasAccessToShops = useSelector(authSelectors.hasAccessToShops);
  const hasAccessToOrganizations = useSelector(
    authSelectors.hasAccessToOrganizations
  );
  const hasAccessToVoucherStacks = useSelector(
    authSelectors.hasAccessToVoucherStacks
  );

  const hasAccessToDeliveryRules = useSelector(
    authSelectors.hasAccessToDeliveryRules
  );
  const hasAccessToChallenges = useSelector(
    authSelectors.hasAccessToChallenges
  );
  const isSuperAdmin = useSelector(authSelectors.isSuperAdmin);

  const hasAccessToMenus = useSelector(authSelectors.hasAccessToMenus);
  const hasAccessToIntegrations = useSelector(
    authSelectors.hasAccessToIntegrations
  );
  const hasAccessToAnalytics = useSelector(authSelectors.hasAccessToAnalytics);
  const router = createBrowserRouter([
    {
      element: <AppUnprotectedRoutes />,
      children: [
        {
          path: "/",
          element: <Navigate to={routePatterns.login} />,
        },
        {
          path: routePatterns.login,
          element: <Login />,
        },
        {
          path: routePatterns.selectOrganization,
          element: <SelectOrganizationList />,
        },
      ],
    },
    {
      element: <AppLayout />,
      children: [
        {
          element: <AppProtectedRoutes />,
          children: [
            {
              path: routePatterns.organizations,
              element: hasAccessToOrganizations ? (
                <OrganizationsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.home,
              element: <Dashboard />,
            },
            {
              path: routePatterns.userList,
              element: hasAccessToUsers ? (
                <UserList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.userProfile,
              element: hasAccessToUsers ? (
                <UserProfile />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.userEventLogList,
              element: hasAccessToUsers ? (
                <UserMonitorEventList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.kitchenCategories,
              element: hasAccessToShops ? (
                <KitchenCategoriesList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.packingItems,
              element: hasAccessToShops ? (
                <PackingItemsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.organizationEventLog,
              element: hasAccessToUsers ? (
                <OrganizationMonitorEventList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.organizations,
              element: hasAccessToOrganizations ? (
                <OrganizationsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.organizationsNew,
              element: hasAccessToOrganizations ? (
                <OrganizationUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.organizationsEdit,
              element: hasAccessToOrganizations ? (
                <OrganizationUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.venueList,
              element: hasAccessToVenues ? (
                <VenueList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.venueEdit,
              element: hasAccessToVenues ? (
                <VenueUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.venueNew,
              element: hasAccessToVenues ? (
                <VenueUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.partnershipList,
              element: hasAccessToPartnerships ? (
                <PartnershipList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.partnershipEdit,
              element: hasAccessToPartnerships ? (
                <PartnershipUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.partnershipAdd,
              element: hasAccessToPartnerships ? (
                <PartnershipUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.promotionCodeList,
              element: hasAccessToPromoCodes ? (
                <PromotionCodeList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.promotionCodeAdd,
              element: hasAccessToPromoCodes ? (
                <PromotionCodeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.promotionCodeEdit,
              element: hasAccessToPromoCodes ? (
                <PromotionCodeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.beaconStatus,
              element: hasAccessToBeacons ? (
                <BeaconList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.beaconStatusView,
              element: hasAccessToBeacons ? (
                <BeaconView />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.emailTemplates,
              element: hasAccessToEmailTemplates ? (
                <EmailTemplateList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.emailTemplateEdit,
              element: hasAccessToEmailTemplates ? (
                <EmailTemplateUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.emailTemplateNew,
              element: hasAccessToEmailTemplates ? (
                <EmailTemplateUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.organizationUISettingsEdit,
              element: hasAccessToOrgUISettings ? (
                <OrganizationSettingsUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.memberCardTypes,
              element: hasAccessToMemberCardTypes ? (
                <MemberCardTypeList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.memberCardTypeEdit,
              element: hasAccessToMemberCardTypes ? (
                <MemberCardTypeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.memberCardTypeNew,
              element: hasAccessToMemberCardTypes ? (
                <MemberCardTypeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.adminUserList,
              element: hasAccessToAdmins ? (
                <AdminUserList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.adminUserEdit,
              element: hasAccessToAdmins ? (
                <AdminUserUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopList,
              element: hasAccessToShops ? (
                <ShopList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopEdit,
              element: hasAccessToShops ? (
                <ShopUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopNew,
              element: hasAccessToShops ? (
                <ShopUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopNewWithVenue,
              element: hasAccessToShops ? (
                <ShopUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductListForShop,
              element: hasAccessToShops ? (
                <ShopSettings />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
              children: [
                {
                  path: routePatterns.shopPaymentProviderAdd,
                  element: <AddNewShopPaymentProvider />,
                },
                {
                  path: routePatterns.shopPaymentProviderUpdate,
                  element: <UpdateShopPaymentProvider />,
                },
              ],
            },
            {
              path: routePatterns.products,
              element: hasAccessToShops ? (
                <ProductsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.productEdit,
              element: hasAccessToShops ? (
                <ProductUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductNew,
              element: hasAccessToShops ? (
                <ProductUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductCategoryList,
              element: hasAccessToShops ? (
                <ProductCategoryList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductCategoryEdit,
              element: hasAccessToShops ? (
                <ProductCategoryUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductCategoryNew,
              element: hasAccessToShops ? (
                <ProductCategoryUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductAdditionsList,
              element: hasAccessToShops ? (
                <ProductAdditionsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductAdditionsNew,
              element: hasAccessToShops ? (
                <ProductAdditionsUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.shopProductAdditionsEdit,
              element: hasAccessToShops ? (
                <ProductAdditionsUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.appHome,
              element: hasAccessToShops ? (
                <AppHome />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.news,
              element: hasAccessToShops ? (
                <NewsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.newsNew,
              element: hasAccessToShops ? (
                <AppNewsUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.newsEdit,
              element: hasAccessToShops ? (
                <AppNewsUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.homepageUpsertListEdit,
              element: hasAccessToShops ? (
                <AppHomepageUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.homepageUpsertListNew,
              element: hasAccessToShops ? (
                <AppHomepageUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.ctasEdit,
              element: hasAccessToShops ? (
                <CtasUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.ctasNew,
              element: hasAccessToShops ? (
                <CtasUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.orderList,
              element: hasAccessToShops ? (
                <OrderList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.orderShow,
              element: hasAccessToShops ? (
                <OrderShow />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.voucherTypesList,
              element: hasAccessToShops ? (
                <VoucherTypeList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.voucherTypesNew,
              element: hasAccessToShops ? (
                <VoucherTypeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.voucherTypesEdit,
              element: hasAccessToShops ? (
                <VoucherTypeUpsert />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.punchCards,
              element:
                hasAccessToShops && hasAccessToVoucherStacks ? (
                  <PunchCardsList />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.punchCardsNew,
              element:
                hasAccessToShops && hasAccessToVoucherStacks ? (
                  <PunchCardUpsert />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.punchCardsEdit,
              element:
                hasAccessToShops && hasAccessToVoucherStacks ? (
                  <PunchCardUpsert />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.challenges,
              element:
                hasAccessToShops && hasAccessToChallenges ? (
                  <ChallengesList />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.challengesEdit,
              element:
                hasAccessToShops && hasAccessToChallenges ? (
                  <ChallengesUpsert />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.challengesNew,
              element:
                hasAccessToShops && hasAccessToChallenges ? (
                  <ChallengesUpsert />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.deliveryrules,
              element: hasAccessToDeliveryRules ? (
                <DeliveryRulesList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.paymentProviderConfigurations,
              element: isSuperAdmin ? (
                <PaymentProviderConfigurations />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.deliveryProviderConfigurations,
              element: isSuperAdmin ? (
                <DeliveryProviderConfigurations />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.menuList,
              element: hasAccessToMenus ? (
                <MenuList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              element: hasAccessToMenus ? (
                <MenuUpsertLayout />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
              children: [
                {
                  path: routePatterns.menuEdit,
                  element: <MenuEdit />,
                },
                {
                  path: routePatterns.menuEditRecommendedProducts,
                  element: <MenuRecommendedProductsUpsert />,
                },
                {
                  path: routePatterns.menuEditMenuAvailability,
                  element: <MenuMenuAvailabilityUpsert />,
                },
                {
                  path: routePatterns.menuEditCampaigns,
                  element: <MenuCampaigns />,
                  children: [
                    {
                      path: routePatterns.menuEditCampaignsAdd,
                      element: <AddMenuCampaigns />,
                    },
                  ],
                },
              ],
            },
            {
              path: routePatterns.autoCampaigns,
              element: <AutoCampaignsLayout />,
              children: [
                {
                  path: routePatterns.campaigns,
                  element: <CampaignsPage />,
                  children: [
                    {
                      path: routePatterns.campaignsEdit,
                      element: <EditCampaignPage />,
                    },
                    {
                      path: routePatterns.campaignsNew,
                      element: <NewCampaignPage />,
                    },
                  ],
                },
                {
                  path: routePatterns.productGroups,
                  element: <ProductGroupsPage />,
                  children: [
                    {
                      path: routePatterns.productGroupEdit,
                      element: <EditProductGroupsPage />,
                    },
                    {
                      path: routePatterns.productGroupNew,
                      element: <NewProductGroupsPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: routePatterns.userTermList,
              element: <UserTermList />,
            },
            {
              path: routePatterns.adyenAccountHolders,
              element: <AdyenAccountHoldersPage />,
              children: [
                {
                  path: routePatterns.adyenAccountHolderNew,
                  element: <NewAdyenAccountHolderPage />,
                },
              ],
            },
            {
              path: routePatterns.adyenAccountHolder,
              element: <AdyenAccountHolderPage />,
              children: [
                {
                  path: routePatterns.adyenAccountHolderEdit,
                  element: <EditAdyenAccountHolderPage />,
                },
                {
                  path: routePatterns.adyenAccountHolderCapabilities,
                  element: <AccountHolderCapabilities />,
                },
                {
                  path: routePatterns.adyenAccountHolderBusinessLinesEdit,
                  element: <EditBusinessLinePage />,
                },
                {
                  path: routePatterns.adyenAccountHolderBusinessLinesNew,
                  element: <NewBusinessLinePage />,
                },
                {
                  path: routePatterns.adyenAccountHolderStore,
                  element: <EditStorePage />,
                },
                {
                  path: routePatterns.adyenAccountHolderStoresNew,
                  element: <NewStorePage />,
                },
                {
                  path: routePatterns.adyenAccountHolderBalanceAccountsNew,
                  element: <NewBalanceAccountPage />,
                },
                {
                  path: routePatterns.adyenAccountHolderBalanceAccountsInspect,
                  element: <InspectBalanceAccountPage />,
                },
                {
                  path:
                    routePatterns.adyenAccountHolderSplitConfigurationInspect,
                  element: <InspectSplitConfiguration />,
                },
              ],
            },
            {
              path: routePatterns.userTermAdd,
              element: <UserTermUpsert />,
            },
            {
              path: routePatterns.userTermEdit,
              element: <UserTermUpsert />,
            },
            {
              path: routePatterns.userTermVersionList,
              element: <UserTermVersionList />,
            },
            {
              path: routePatterns.userTermVersionAdd,
              element: <UserTermVersionUpsert />,
            },
            {
              path: routePatterns.userTermVersionEdit,
              element: <UserTermVersionUpsert />,
            },
            {
              path: routePatterns.voucherTypesNew,
              element: <VoucherTypeUpsert />,
            },
            {
              path: routePatterns.productTags,
              element: <ProductTagsList />,
            },
            {
              path: routePatterns.integrationsList,
              element: hasAccessToIntegrations ? (
                <IntegrationsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.reports,
              element:
                isSuperAdmin || hasAccessToAnalytics ? (
                  <Reports />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.reportDetails,
              element:
                isSuperAdmin || hasAccessToAnalytics ? (
                  <ReportDetails />
                ) : (
                  <Navigate to={routePatterns.home} />
                ),
            },
            {
              path: routePatterns.integrationsList,
              element: hasAccessToIntegrations ? (
                <IntegrationsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.apiKeysList,
              element: isSuperAdmin ? (
                <ApiKeysList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
            {
              path: routePatterns.kioskConfigurations,
              element: hasAccessToShops ? (
                <KioskConfigurationsList />
              ) : (
                <Navigate to={routePatterns.home} />
              ),
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ]);

  return (
    <AppShell>
      <RouterProvider router={router} />
    </AppShell>
  );
};

export default App;
